import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminDeleteComment, adminDeleteVideo, fetchUserDetail, fetchUserComments, fetchUserVideos, patchUserAdminFlags } from '../lib/superadminUsersApi';

const UserDetail: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [videos, setVideos] = useState<any>({ total: 0, rows: [] });
  const [comments, setComments] = useState<any>({ total: 0, rows: [] });

  // We allow route like /users/:id; if users index links by email, resolve via backend later if needed
  const idParam = params.id || '';
  const idNum = Number(idParam);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const detail = await fetchUserDetail(idNum);
        setData(detail);
        setIsAdmin(Boolean(detail?.user?.isAdmin));
        setIsSuperAdmin(Boolean(detail?.user?.isSuperAdmin));
        const vids = await fetchUserVideos(idNum, { limit: 5, order: 'desc', sort: 'createdAt' });
        const comms = await fetchUserComments(idNum, { limit: 5, order: 'desc', sort: 'createdAt' });
        // Guard against backends returning more than requested; only show recent N here
        const normalize = (res: any, n: number) => Array.isArray(res)
          ? { total: res.length, rows: res.slice(0, n) }
          : { total: Number(res?.total ?? 0), rows: Array.isArray(res?.rows) ? res.rows.slice(0, n) : [] };
        setVideos(normalize(vids, 5));
        setComments(normalize(comms, 5));
      } catch (e: any) {
        setError(e?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [idNum]);

  const updateAdminFlags = async (next: { isAdmin: boolean; isSuperAdmin: boolean }) => {
    try {
      setIsAdmin(next.isAdmin);
      setIsSuperAdmin(next.isSuperAdmin);
      await patchUserAdminFlags(idNum, next);
    } catch (e: any) {
      setError(e?.message || 'Failed to update admin flags');
    }
  };

  const onDeleteVideo = async (id: number) => {
    if (!window.confirm('Delete this video?')) return;
    await adminDeleteVideo(id);
    const vids = await fetchUserVideos(idNum, { limit: 5, order: 'desc', sort: 'createdAt' });
    setVideos(Array.isArray(vids) ? { total: vids.length, rows: vids.slice(0, 5) } : { total: vids.total, rows: vids.rows.slice(0, 5) });
  };

  const onDeleteComment = async (id: number) => {
    if (!window.confirm('Delete this comment?')) return;
    await adminDeleteComment(id);
    const comms = await fetchUserComments(idNum, { limit: 5, order: 'desc', sort: 'createdAt' });
    setComments(Array.isArray(comms) ? { total: comms.length, rows: comms.slice(0, 5) } : { total: comms.total, rows: comms.rows.slice(0, 5) });
  };

  if (loading) return <div>Loading…</div>;
  if (error) return <div className="text-red-600">{error}</div>;
  if (!data) return <div>No data</div>;

  const u = data.user;

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xl font-semibold">{u.firstName} {u.lastName}</div>
            <div className="text-gray-600">{u.email}</div>
          </div>
          <div className="flex items-center gap-4">
            <label className="text-sm text-gray-700 flex items-center gap-2">
              <input
                type="checkbox"
                checked={isAdmin}
                onChange={(e) => {
                  const nextIsAdmin = e.target.checked;
                  const nextIsSuperAdmin = nextIsAdmin ? isSuperAdmin : false;
                  updateAdminFlags({
                    isAdmin: nextIsAdmin,
                    isSuperAdmin: nextIsSuperAdmin,
                  });
                }}
              />
              Admin
            </label>
            <label className="text-sm text-gray-700 flex items-center gap-2">
              <input
                type="checkbox"
                checked={isSuperAdmin}
                onChange={(e) =>
                  updateAdminFlags({
                    isAdmin: e.target.checked ? true : isAdmin,
                    isSuperAdmin: e.target.checked,
                  })
                }
              />
              SuperAdmin
            </label>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold">Recent Videos</div>
          <button className="text-blue-600 text-sm" onClick={() => navigate(`/users/${idNum}/videos`)}>See All</button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Title</th>
                <th className="py-2 pr-4">Comments</th>
                <th className="py-2 pr-4">Uploaded</th>
                <th className="py-2 pr-4">Shared</th>
                <th className="py-2 pr-4">Workspace</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {videos.rows.map((v: any) => (
                <tr key={v.id} className="border-t">
                  <td className="py-2 pr-4 text-blue-600"><a href={v.videoUrl} target="_blank" rel="noreferrer">{v.title}</a></td>
                  <td className="py-2 pr-4">{v.commentCount}</td>
                  <td className="py-2 pr-4">{new Date(v.createdAt).toLocaleString()}</td>
                  <td className="py-2 pr-4">{v.sharedWithCount}</td>
                  <td className="py-2 pr-4">{v.workspace}</td>
                  <td className="py-2 pr-4"><button className="text-red-600" onClick={() => onDeleteVideo(v.id)}>Delete</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold">Recent Comments</div>
          <button className="text-blue-600 text-sm" onClick={() => navigate(`/users/${idNum}/comments`)}>See All</button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Comment</th>
                <th className="py-2 pr-4">Video</th>
                <th className="py-2 pr-4">Owner</th>
                <th className="py-2 pr-4">Timestamp</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {comments.rows.map((c: any) => (
                <tr key={c.id} className="border-t">
                  <td className="py-2 pr-4">{c.content || 'Audio comment'}</td>
                  <td className="py-2 pr-4 text-blue-600"><a href={`/videos/${c.videoId}`} target="_blank" rel="noreferrer">{c.videoTitle || `Video ${c.videoId}`}</a></td>
                  <td className="py-2 pr-4">{c.videoOwner}</td>
                  <td className="py-2 pr-4">{new Date(c.createdAt).toLocaleString()}</td>
                  <td className="py-2 pr-4"><button className="text-red-600" onClick={() => onDeleteComment(c.id)}>Delete</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="font-semibold mb-3">Workspaces</div>
        <ul className="list-disc pl-5 text-sm">
          {data.workspaces.map((w: any) => (
            <li key={w.id} className="flex items-center justify-between py-1">
              <span>{w.name}</span>
              {/* Removal handled from workspace page for now; can add direct call to DELETE /workspaces/:workspaceId/users/:userId */}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default UserDetail;

